import querystring from 'querystring';

import {
  getShortFormattedDateWithSlashes,
  getDateFromString,
  getDateKey,
} from '../../../../../utils/date-utils/dateUtils';
import { LegislationInfos } from '../../../legislation.types';
import { getParametersFromUrl } from '../../../../../utils/url-utils/urlUtils';
import makeQueryParamsUtil from '../../../../../../../shared/makeQueryParamsUtil';
import { shouldBeSortedByChrono } from '../components/search-query-section/utils/utils';

export const linkTypes = {
  start: [
    {
      id: 'createdBy',
      labelId: 'app.pages.legislation.linked.createdBy',
      source: 'legislation-start-end-text',
    },
    {
      id: 'codifiedBy',
      labelId: 'app.pages.legislation.linked.codifiedBy',
      source: 'legislation-start-end-text',
    },
    {
      id: 'modifiedBy',
      labelId: 'app.pages.legislation.linked.modifiedBy',
      source: 'legislation-start-end-text',
    },
  ],
  end: [
    {
      id: 'abrogatedBy',
      labelId: 'app.pages.legislation.linked.abrogatedBy',
      source: 'legislation-start-end-text',
    },
    {
      id: 'transferredBy',
      labelId: 'app.pages.legislation.linked.transferredBy',
      source: 'legislation-start-end-text',
    },
    {
      id: 'outdatedBy',
      labelId: 'app.pages.legislation.linked.outdatedBy',
      source: 'legislation-start-end-text',
    },
    {
      id: 'separatedBy',
      labelId: 'app.pages.legislation.linked.separatedBy',
      source: 'legislation-start-end-text',
    },
    {
      id: 'cancelledBy',
      labelId: 'app.pages.legislation.linked.cancelledBy',
      source: 'legislation-start-end-text',
    },
  ],
  new: [{ id: 'newTexts', source: 'legislation-old-new-text' }],
  old: [{ id: 'oldTexts', source: 'legislation-old-new-text' }],
};

export const getFormattedLegislationsVersions = (
  legislationInfos: LegislationInfos,
  versions: LegislationInfos[],
  intl: any,
) =>
  versions.map(({ articleId: id, startingDate, endingDate }) => {
    const isCurrentLegislation = id === legislationInfos.articleId;

    const setContent = () => {
      if (startingDate && !endingDate)
        return intl.formatMessage(
          {
            id: 'page/LegislationPage.tsx/5',
          },
          {
            startDate: getShortFormattedDateWithSlashes(startingDate),
            span: (msg: string) => `<span class="vis-item-date">${msg}</span>`,
          },
        );
      if (startingDate && endingDate)
        return intl.formatMessage(
          {
            id: 'page/LegislationPage.tsx/7',
          },
          {
            startDate: getShortFormattedDateWithSlashes(startingDate),
            endDate: getShortFormattedDateWithSlashes(endingDate),
            span: (msg: string) => `<span class="vis-item-date">${msg}</span>`,
          },
        );
      return intl.formatMessage({
        id: 'page/LegislationPage.tsx/4',
      });
    };

    return {
      id,
      start: startingDate,
      content: setContent(),
      current: isCurrentLegislation,
      title: !isCurrentLegislation
        ? intl.formatMessage({
            id: 'page/LegislationPage.tsx/6',
          })
        : undefined,
    };
  });

// date key used for navigation between article and codes page
export const getFromUrlOrBuildDateVersionKey = (
  search: string,
  endingDate: string | null,
) => {
  const params = getParametersFromUrl(search);
  if (params.dateKey) {
    return params.dateKey;
  }
  // same as legifrance, which uses the version of the article ending date minus 1 day for reference date
  const date = endingDate
    ? getDateFromString(endingDate).subtract(1, 'days').toDate()
    : new Date();
  return getDateKey(date);
};

export const buildSearchModalURL = ({
  query,
  articleIds,
  filterValue = '',
  type,
  version = '',
  modal,
}: {
  query: string;
  articleIds: string[];
  filterValue?: string | { legislativeFolder: string };
  type: string;
  version?: string;
  modal: string;
}) => {
  const legislativeFolderFilter =
    typeof filterValue === 'object' ? filterValue.legislativeFolder : '';
  const searchQueryParams = {
    ...makeQueryParamsUtil({
      query,
      restrict_to_law_article_ids: articleIds,
      chrono: shouldBeSortedByChrono(filterValue),
      type,
      ...(filterValue === 'parliamentary_questions' && {
        cats: {
          publisher: [
            ['Organismes administratifs et institutions'],
            [
              'Organismes administratifs et institutions',
              'Questions parlementaires',
            ],
          ],
        },
      }),
      ...(filterValue === 'bofip' && {
        cats: {
          publisher: [
            ['Organismes administratifs et institutions'],
            ['Organismes administratifs et institutions', 'BOFIP'],
          ],
        },
      }),
      ...(filterValue === 'cass' && { cats: { juridiction: ['CASS'] } }),
      ...(filterValue === 'ce' && { cats: { juridiction: ['CE'] } }),
      ...(legislativeFolderFilter.length && {
        cats: { pwLegislativeFolder: [legislativeFolderFilter] },
      }),
    }),
    ...(version && { version }),
    modal,
  };
  return querystring.stringify(searchQueryParams);
};

// Limit count displaying to 500
export const transformCount = (count: number | string) =>
  Number(count) > 500 ? '+500' : count;
